/**
 * AuditTrailPage — Paramètres › Piste d'audit (Batch #14.290).
 * Qui a modifié quoi, et quand : journal de toutes les actions (admin, clients du portail, liens publics), filtres, détail, export CSV, conservation.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, History, Loader2, RefreshCw, Settings2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, downloadPdf, errMsg } from "./comptabilite/_shared";
import AuditFilters from "./audit/AuditFilters";
import AuditTable from "./audit/AuditTable";
import AuditDetail from "./audit/AuditDetail";
import { monthRange } from "@/components/ListTools";
import AuditSettingsModal from "./audit/AuditSettingsModal";

const PAGE = 50;
const EMPTY = { q: "", actor_id: "", actor_type: "", module: "", action: "", date_from: "", date_to: "", ok: "", master_id: "" };
const monthRange_ = () => { const r = monthRange(); return { date_from: r.from, date_to: r.to }; };

function Kpi({ label, value, tone = "slate", testId }) {
  const tones = { slate: "bg-white border-slate-200 text-slate-900", rose: "bg-rose-50 border-rose-200 text-rose-900", sky: "bg-sky-50 border-sky-200 text-sky-900", emerald: "bg-emerald-50 border-emerald-200 text-emerald-900" };
  return (
    <div className={`border rounded-xl p-3 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-lg font-bold tabular-nums mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

export default function AuditTrailPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ ...EMPTY, ...monthRange_(), entity_id: sp.get("entity_id") || "", master_id: sp.get("master_id") || "" });
  const [facets, setFacets] = useState({ modules: [], actors: [], actions: [] });
  const [stats, setStats] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const params = useCallback(() => Object.fromEntries(Object.entries(f).filter(([, v]) => v)), [f]);

  const loadMeta = useCallback(() => {
    api.get("/audit/facets").then((r) => setFacets(r.data)).catch(() => {});
    api.get("/audit/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async (skip = 0) => {
    setBusy(true);
    try {
      const r = await api.get("/audit", { params: { ...params(), limit: PAGE, skip } });
      setData((p) => (skip && p ? { ...r.data, items: [...p.items, ...r.data.items] } : r.data));
    } catch (e) { toast.error(errMsg(e, "Chargement du journal impossible")); setData({ items: [], total: 0 }); }
    finally { setBusy(false); }
  }, [params]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { const t = setTimeout(() => load(0), f.q ? 300 : 0); return () => clearTimeout(t); }, [load, f.q]);

  const refresh = () => { load(0); loadMeta(); };
  const exportCsv = () => downloadPdf(`/audit/export.csv?${new URLSearchParams(params()).toString()}`, "piste-audit.csv", setExporting);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4" data-testid="audit-page">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate("/admin/settings")} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center transition active:scale-90" aria-label="Retour" data-testid="audit-back-btn">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex items-center gap-2 text-sm text-slate-500 flex-1 min-w-0">
          <span className="font-medium hidden sm:inline">Paramètres</span>
          <span className="text-slate-300 hidden sm:inline">›</span>
          <span className="font-bold text-slate-800 inline-flex items-center gap-1.5 truncate"><History className="w-4 h-4 shrink-0 text-slate-600" /> Piste d'audit</span>
        </div>
        <Btn tone="slate" icon={RefreshCw} onClick={refresh} testId="audit-refresh-btn">Actualiser</Btn>
        <Btn tone="slate" icon={Download} onClick={exportCsv} busy={exporting} testId="audit-export-btn">Exporter CSV</Btn>
        <Btn tone="slate" icon={Settings2} onClick={() => setSettingsOpen(true)} testId="audit-settings-btn">Conservation</Btn>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="audit-stats">
        <Kpi label="Dernières 24 h" value={stats?.last_24h} tone="sky" testId="audit-kpi-24h" />
        <Kpi label="7 derniers jours" value={stats?.last_7d} testId="audit-kpi-7d" />
        <Kpi label="Erreurs · 7 j" value={stats?.errors_7d} tone={stats?.errors_7d ? "rose" : "slate"} testId="audit-kpi-errors" />
        <Kpi label="Acteurs actifs · 7 j" value={stats?.actors_7d} tone="emerald" testId="audit-kpi-actors" />
        <Kpi label="Entrées au total" value={stats?.total} testId="audit-kpi-total" />
      </div>

      <AuditFilters f={f} setF={setF} facets={facets} count={data?.total} reset={() => setF({ ...EMPTY })} />

      {data === null ? (
        <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : (
        <AuditTable items={data.items} onOpen={setDetail} onFilterActor={(a) => setF((p) => ({ ...p, actor_id: a.id || "", actor_type: a.id ? "" : a.type }))} onFilterModule={(m) => setF((p) => ({ ...p, module: m }))} />
      )}

      {data && data.items.length < data.total && (
        <div className="text-center">
          <Btn tone="slate" onClick={() => load(data.items.length)} busy={busy} testId="audit-load-more">Charger les {Math.min(PAGE, data.total - data.items.length)} suivantes ({data.items.length}/{data.total})</Btn>
        </div>
      )}

      <p className="text-[11px] text-slate-400">
        Chaque action qui modifie des données (création, modification, suppression, report, envoi, connexion…) est consignée automatiquement avec son auteur, son adresse IP et le détail transmis.
        Les mots de passe et jetons sont masqués. Le journal est en lecture seule.
      </p>

      {detail && <AuditDetail entry={detail} onClose={() => setDetail(null)} onFilterEntity={(id) => { setF((p) => ({ ...p, entity_id: id })); setDetail(null); }} />}
      {settingsOpen && <AuditSettingsModal onClose={() => setSettingsOpen(false)} onChanged={loadMeta} stats={stats} />}
    </div>
  );
}
